import { useState, useMemo } from "react";
import { ArrowRight, Clock, CurrencyDollar, Lightning } from "@phosphor-icons/react";
import { trackConversion } from "../../lib/analytics";
import { scrollTo } from "./utils";

// ROI Calculator section — interactive: user drags 3 sliders, live estimates
// of monthly repetitive-work hours + labor cost + savings range appear on the
// right. Every result labelled "estimates only, not guarantees" per user spec.
export default function ROICalculatorSection() {
  const [employees, setEmployees] = useState(4);
  const [hoursPerWeek, setHoursPerWeek] = useState(10);
  const [hourlyCost, setHourlyCost] = useState(28);

  const totals = useMemo(() => {
    const weeksPerMonth = 4.33;
    const monthlyHours = Math.round(employees * hoursPerWeek * weeksPerMonth);
    const monthlyCost = Math.round(monthlyHours * hourlyCost);
    // Time-savings range: conservative 25% – 60% of repetitive hours.
    const savingsLow = Math.round(monthlyHours * 0.25);
    const savingsHigh = Math.round(monthlyHours * 0.6);
    return { monthlyHours, monthlyCost, savingsLow, savingsHigh };
  }, [employees, hoursPerWeek, hourlyCost]);

  return (
    <section
      id="roi"
      data-testid="roi-section"
      className="border-b border-white/10 py-20 sm:py-28 scroll-mt-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="overline mb-3 text-[#FF9764]">Return on time</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            What could your team accomplish with{" "}
            <span className="text-[#FF5A1F]">more time</span>?
          </h2>
          <p className="text-neutral-400 mt-4 text-sm sm:text-base leading-relaxed">
            A rough estimate of how much repetitive office work adds up across your operation.
            Adjust the sliders to match your team.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          <div className="lg:col-span-3 rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 space-y-6">
            <ROISlider
              label="Number of office employees"
              value={employees} min={1} max={30} step={1}
              onChange={setEmployees}
              testid="roi-employees"
              suffix={employees === 1 ? "employee" : "employees"}
            />
            <ROISlider
              label="Average repetitive-work hours per employee per week"
              value={hoursPerWeek} min={1} max={40} step={1}
              onChange={setHoursPerWeek}
              testid="roi-hours"
              suffix="hrs / week"
            />
            <ROISlider
              label="Estimated hourly labor cost"
              value={hourlyCost} min={15} max={90} step={1}
              onChange={setHourlyCost}
              testid="roi-cost"
              prefix="$" suffix="/ hr"
            />
          </div>

          <div className="lg:col-span-2 space-y-3">
            <ROIStat
              testid="roi-monthly-hours"
              label="Total repetitive hours / month"
              value={totals.monthlyHours.toLocaleString()}
              unit="hrs"
              icon={Clock}
            />
            <ROIStat
              testid="roi-monthly-cost"
              label="Estimated monthly labor cost"
              value={`$${totals.monthlyCost.toLocaleString()}`}
              icon={CurrencyDollar}
              highlight
            />
            <ROIStat
              testid="roi-savings-range"
              label="Potential time savings range"
              value={`${totals.savingsLow.toLocaleString()}–${totals.savingsHigh.toLocaleString()}`}
              unit="hrs / month"
              icon={Lightning}
            />
            <p className="text-[11px] text-neutral-500 leading-relaxed pt-1">
              All figures are estimates for planning purposes only — not guarantees.
              Actual results vary by workflow, team, and scope of automation.
            </p>
            <a
              href="#assessment"
              onClick={(e) => {
                e.preventDefault();
                trackConversion("roi_cta_click", { hours: totals.monthlyHours });
                scrollTo("assessment");
              }}
              data-testid="roi-cta"
              className="btn-primary rounded-full px-5 py-3 text-sm inline-flex items-center justify-center gap-2 w-full"
            >
              Request a Personalized Savings Review
              <ArrowRight size={14} weight="bold" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function ROISlider({ label, value, min, max, step, onChange, prefix = "", suffix = "", testid }) {
  return (
    <div>
      <div className="flex justify-between items-baseline mb-2">
        <label className="text-sm text-neutral-300">{label}</label>
        <span className="font-mono text-lg font-semibold text-white">
          {prefix}
          {value}
          {suffix ? <span className="text-xs text-neutral-500 font-normal ml-1">{suffix}</span> : null}
        </span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={`${testid}-slider`}
        aria-label={label}
        className="w-full accent-[#FF5A1F] cursor-pointer"
      />
      <div className="flex justify-between text-[10px] text-neutral-600 mt-1 font-mono">
        <span>{prefix}{min}</span>
        <span>{prefix}{max}</span>
      </div>
    </div>
  );
}

function ROIStat({ label, value, unit, icon: Icon, highlight, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border p-5 ${
        highlight
          ? "border-[#FF5A1F]/40 bg-[#FF5A1F]/5"
          : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={highlight ? "text-[#FF5A1F]" : "text-neutral-500"} />
        <div className="overline text-neutral-400">{label}</div>
      </div>
      <div className="font-mono text-2xl sm:text-3xl font-semibold">
        {value}
        {unit ? <span className="text-neutral-500 text-sm font-normal ml-1.5">{unit}</span> : null}
      </div>
    </div>
  );
}
